import { createElement, lazy, type ComponentProps, type ComponentType, type LazyExoticComponent, type ReactElement } from "react";

// A part of the app loaded when first needed — the live screen, a
// recording's player — whose file a newer build has replaced: a page opened
// before a release asks for a chunk that is no longer there, and the relay
// answers its index.html instead. Reloading once takes the new build; a
// second failure soon after is not that, and is thrown to the page's
// boundary (components/PageBoundary) rather than reloading for ever.

const ReloadedKey = "share.chunk-reload";
const ReloadWindowMs = 30_000;

/** Whether an error is a lazily loaded chunk that did not load, in Chrome, Firefox or Safari. */
export function isChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(msg);
}

/** Reload the page for a chunk that did not load, unless it was reloaded for one moments ago. */
export function reloadForChunk(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(ReloadedKey) ?? 0);
    if (now - last < ReloadWindowMs) return false;
    sessionStorage.setItem(ReloadedKey, String(now));
  } catch {
    // storage blocked: no guard against reloading again, so do not reload
    return false;
  }
  window.location.reload();
  return true;
}

// the parts that failed and were not reloaded for, each asked for again on
// the next page (retryFailedChunks)
const failed = new Set<() => void>();

/**
 * The parts that failed, asked for again from the next draw on: the page's
 * boundary calls it as the path changes (components/PageBoundary), so the
 * next page draws its own rather than the last one's failure. Not sooner:
 * React draws a part that threw once more before it gives up to the
 * boundary, and a new attempt there would suspend instead, for ever.
 */
export function retryFailedChunks(): void {
  for (const retry of failed) retry();
  failed.clear();
}

/**
 * React.lazy, reloading once for a chunk a newer build has replaced. A
 * failure it does not reload for is thrown to the page's boundary and,
 * React.lazy keeping a rejection for good, replaced by a new lazy on the
 * next page (retryFailedChunks).
 */
// any, as React.lazy bounds it: a component of some props, whichever
export function lazyChunk<T extends ComponentType<any>>(load: () => Promise<{ default: T }>): (props: ComponentProps<T>) => ReactElement {
  const fresh = (): LazyExoticComponent<T> =>
    lazy(() =>
      load().catch((err: unknown) => {
        // the page is going: nothing to draw until it has
        if (isChunkError(err) && reloadForChunk()) return new Promise<{ default: T }>(() => {});
        failed.add(retry);
        throw err;
      }),
    );
  let current = fresh();
  const retry = () => {
    current = fresh();
  };
  return (props) => createElement(current, props);
}
